import Controller, { OnAfterNavigate, OnBeforeSubmit,  OnChange, OnMount, OnUnmount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { AutocompleteAPI, CheckboxAPI, CheckboxProps, DateFieldAPI, FormAPI, InputAPI, LocatorAPI, RadioGroupAPI, SelectableItem, TextAreaAPI, ToastService, WizardAPI } from '@philips-internal/oswald-ui-components';
import { ChangeEvent, MountEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';

import { Inject } from '@odinjs/odin';

import moment from 'moment';

import BaseController from '../../../BaseController';
import { Endpoint } from '../../../../constants/endpoints';
import { FragmentNames, HealthHistoryDiseasesCreateAliases } from '../../../../codegen';
import { DiseasesType, DomainType, HealthHistoryDiseasesResponse, HealthHistoryType, RadioType, SaeSearch, SearchType } from '../../health-history-types';
import { HealthProfessionalUser } from '../../../../app/authentication';
import { DiseasesRadioGroupId } from '../radio-group-data/diseases-radio-group-expressions';
import { HealthHistoryListContext } from '../../service/health-history-list-service';
import DialogService from '../../../../services/dialog-service';
import InformationBarUtils from '../../../../../src/controllers/commons/header/information-bar-header-utils';
import { LocatorTypes } from '../../../../../src/controllers/locator/locator-controller';
import timeService from '../../../../../src/services/time-service';
import { ISO_FULL_DATE_FORMAT } from '../../../../../src/constants/date-utils';
import { BooleanUtils } from '../../../../utils/boolean-utils';

enum WizardSteps {
  STEP_ONE = 'stepOne',
  STEP_TWO = 'stepTwo',
  STEP_THREE = 'stepThree'
}

@Controller({ component: HealthHistoryDiseasesCreateAliases.LAYOUT_HEALTH_HISTORY_DISEASES_CREATE, fragment: FragmentNames.HEALTH_HISTORY_DISEASES_CREATE })
export default class HealthHistoryDiseasesCreateController extends BaseController {

  @Inject(HealthHistoryDiseasesCreateAliases.INPUT_RECORD_DATE)
  private recordDateInputApi!: InputAPI;

  @Inject(HealthHistoryDiseasesCreateAliases.FORM_ONE)
  private formOneDiseasesCreateAPI!: FormAPI;

  @Inject(HealthHistoryDiseasesCreateAliases.FORM_TWO)
  private formTwoDiseasesCreateAPI!: FormAPI;

  @Inject(HealthHistoryDiseasesCreateAliases.FORM_THREE)
  private formThreeDiseasesCreateAPI!: FormAPI;

  @Inject(HealthHistoryDiseasesCreateAliases.WIZARD)
  private wizardAPI!: WizardAPI;

  @Inject(HealthHistoryDiseasesCreateAliases.CHECKBOX_DENIES_DISEASES)
  private checkboxDeniesdDiseasesAPI!: CheckboxAPI;

  @Inject(HealthHistoryDiseasesCreateAliases.CHECKBOX_DISPLAY_PATIENT_ALERTS)
  private checkboxAlertsAPI!: CheckboxAPI;

  @Inject(HealthHistoryDiseasesCreateAliases.CHECKBOX_INSERT_DIAGNOSIS_LIST)
  private checkboxInsertDiagnosisApi!: CheckboxAPI;

  @Inject(HealthHistoryDiseasesCreateAliases.INPUT_CID)
  private cidInputAPI!: InputAPI;

  @Inject(HealthHistoryDiseasesCreateAliases.LOCATOR_CID)
  private locatorCidAPI!: LocatorAPI

  @Inject(HealthHistoryDiseasesCreateAliases.INPUT_DISEASESFREE)
  private inputDiseaseFreeAPI!: InputAPI

  @Inject(HealthHistoryDiseasesCreateAliases.INPUT_SAE)
  private saeInputAPI!: InputAPI;

  @Inject(HealthHistoryDiseasesCreateAliases.LOCATOR_SAE)
  private locatorSaeAPI!: LocatorAPI

  @Inject(HealthHistoryDiseasesCreateAliases.RADIO_GROUP_HIGHLIGHT_INTENSITY_SEVERITY)
  private radioGroupIntensitySeveryAPI!: RadioGroupAPI;

  @Inject(HealthHistoryDiseasesCreateAliases.AUTOCOMPLETE_SITUATION)
  private autocompleteSituationApi!: AutocompleteAPI;

  @Inject(HealthHistoryDiseasesCreateAliases.AUTOCOMPLETE_SECURITYLEVEL)
  private autocompleteSecurityLevelApi!: AutocompleteAPI;

  @Inject(HealthHistoryDiseasesCreateAliases.DATE_FIELD_START_DATE)
  private dateFieldStartDateApi!: DateFieldAPI;

  @Inject(HealthHistoryDiseasesCreateAliases.DATE_FIELD_END_DATE)
  private dateFieldEndDateApi!: DateFieldAPI;

  @Inject(HealthHistoryDiseasesCreateAliases.AUTOCOMPLETE_PATHOLOGIE)
  private autocompletePathologieApi!: AutocompleteAPI;

  @Inject(HealthHistoryDiseasesCreateAliases.CHECKBOX_INSERT_PROBLEMS_LIST)
  private checkboxInsertProblemsApi!: CheckboxAPI;

  @Inject(HealthHistoryDiseasesCreateAliases.TEXT_AREA_APPROACH)
  private textAreaApproachApi!: TextAreaAPI;

  @OnValidate(HealthHistoryDiseasesCreateAliases.FORM_ONE)
  handleOnValidate(validationFormOne: ValidateEvent): void {
    this.handleStepDone(validationFormOne.valid, WizardSteps.STEP_ONE);
  }

  @OnValidate(HealthHistoryDiseasesCreateAliases.FORM_TWO)
  handleOnSecondValidate(validationFormTwo: ValidateEvent): void {
    this.handleStepDone(validationFormTwo.valid, WizardSteps.STEP_TWO);
  }

  @OnValidate(HealthHistoryDiseasesCreateAliases.FORM_THREE)
  handleOnTirdValidate(validationFormThree: ValidateEvent): void {
    this.handleStepDone(validationFormThree.valid, WizardSteps.STEP_THREE);
  }

  private handleStepDone(isStepDone: boolean, step: WizardSteps): void {
    if (isStepDone) {
      this.wizardAPI.setStepDone(step, true);
    }
  }

  @OnAfterNavigate(HealthHistoryDiseasesCreateAliases.WIZARD)
  handleOnMountWizardStepTwo(): void {
    this.wizardAPI.setStepDone(WizardSteps.STEP_THREE, true);
    this.wizardAPI.setStepDone(WizardSteps.STEP_TWO, true);
  }

  @OnMount({ target: HealthHistoryDiseasesCreateAliases.FORM_ONE })
  handleMountForm(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    this.context.set({ encounterId, locator: LocatorTypes.SAE_SEARCH, patientId });
    HealthHistoryListContext.currentTab = HealthHistoryType.DISEASES;
    InformationBarUtils.collapsedBar = true;
    timeService.getCurrentDateTime()
      .then(date => this.recordDateInputApi.setValue(date))
      .catch(() => this.recordDateInputApi.setValue(moment().format(ISO_FULL_DATE_FORMAT)))
    this.checkboxAlertsAPI.setValue(true);
  }

  @OnMount({ target: HealthHistoryDiseasesCreateAliases.RADIO_GROUP_HIGHLIGHT_INTENSITY_SEVERITY })
  handleMountRadioGroupHighlightIntensitySeverity(event: MountEvent): void {
    const radioGroup = event.target as RadioGroupAPI;
    this.setRadioGroupData(DiseasesRadioGroupId.HIGHLIGHT_INTENSITY_SEVERITY, radioGroup)
  }

  @OnChange({ target: [HealthHistoryDiseasesCreateAliases.FIELD_CID] })
  handleClassificationChange(event: ChangeEvent<DiseasesType>): void {
    const value = event.value;
    const oldValue = event.oldValue;

    if (oldValue === value) {
      return;
    }

    if (value?.diseaseCode) {
      this.disableSaeFields();

      this.inputDiseaseFreeAPI.setEnabled(false)
      this.checkboxDeniesdDiseasesAPI.setEnabled(false);
      this.checkboxInsertDiagnosisApi.setEnabled(true);
    } else {
      this.enableSaeFields();

      this.inputDiseaseFreeAPI.setEnabled(true);
      this.checkboxInsertDiagnosisApi.setEnabled(false);
      this.checkboxInsertDiagnosisApi.setValue(false);
      this.checkboxDeniesdDiseasesAPI.setEnabled(true);
    }
  }

  @OnChange({ target: [HealthHistoryDiseasesCreateAliases.FIELD_SAE] })
  handleSaeChange(event: ChangeEvent<SaeSearch>): void {
    const value = event.value;
    const oldValue = event.oldValue;

    if (oldValue === value) {
      return;
    }

    if (value?.id) {
      this.disableCidFields();

      this.inputDiseaseFreeAPI.setEnabled(false);
      this.checkboxDeniesdDiseasesAPI.setEnabled(false);
    } else {
      this.enableCidFields();
      this.inputDiseaseFreeAPI.setEnabled(true);
      this.checkboxDeniesdDiseasesAPI.setEnabled(true);
    }
  }

  @OnChange({ target: HealthHistoryDiseasesCreateAliases.FIELD_DISEASEFREE })
  handleDiseaseFree(event: ChangeEvent<InputAPI>): void {
    const value = event.value;
    const oldValue = event.oldValue;

    if (oldValue === value) {
      return;
    }

    if (value) {
      this.disableCidFields();
      this.disableSaeFields();

      this.checkboxDeniesdDiseasesAPI.setEnabled(false);
    } else {
      this.enableCidFields();
      this.enableSaeFields();

      this.checkboxDeniesdDiseasesAPI.setEnabled(true);
    }
  }

  @OnChange({ target: HealthHistoryDiseasesCreateAliases.CHECKBOX_DENIES_DISEASES })
  handleDeniesDiseasesChange(event: ChangeEvent<CheckboxProps>): void {
    const value = event.value;

    this.enableOrDisableFieldsAfterDeniesDisease(value);
  }

  private disableCidFields(): void {
    this.locatorCidAPI.setEnabled(false);
    this.cidInputAPI.setEnabled(false);
    this.locatorCidAPI.setInputEnabled(false);
  }

  private enableCidFields(): void {
    this.locatorCidAPI.setEnabled(true);
    this.cidInputAPI.setEnabled(true);
    this.locatorCidAPI.setInputEnabled(true);
  }

  private disableSaeFields(): void {
    this.locatorSaeAPI.setEnabled(false);
    this.saeInputAPI.setEnabled(false);
    this.locatorSaeAPI.setInputEnabled(false);
  }

  private enableSaeFields(): void {
    this.locatorSaeAPI.setEnabled(true);
    this.saeInputAPI.setEnabled(true);
    this.locatorSaeAPI.setInputEnabled(true);
  }

  private enableOrDisableFieldsAfterDeniesDisease(value: CheckboxProps): void {
    if (value) {
      this.disableCidFields();
      this.disableSaeFields();

      this.autocompleteSituationApi.setValue(undefined);
      this.autocompleteSituationApi.setEnabled(false);
      this.inputDiseaseFreeAPI.setEnabled(false);
      this.autocompleteSecurityLevelApi.setValue(undefined);
      this.autocompleteSecurityLevelApi.setEnabled(false);
      this.dateFieldStartDateApi.setValue('');
      this.dateFieldStartDateApi.setEnabled(false);
      this.dateFieldEndDateApi.setValue('');
      this.dateFieldEndDateApi.setEnabled(false);
      this.autocompletePathologieApi.setValue(undefined);
      this.autocompletePathologieApi.setEnabled(false);

      this.radioGroupIntensitySeveryAPI.setEnabled(false);
      this.checkboxInsertProblemsApi.setValue(false);
      this.checkboxInsertProblemsApi.setEnabled(false);
      this.checkboxInsertDiagnosisApi.setValue(false);
      this.checkboxInsertDiagnosisApi.setEnabled(false);
      this.textAreaApproachApi.setValue('');
      this.textAreaApproachApi.setEnabled(false);
    } else {
      this.enableCidFields();
      this.enableSaeFields();

      this.autocompleteSituationApi.setEnabled(true);
      this.inputDiseaseFreeAPI.setEnabled(true);
      this.autocompleteSecurityLevelApi.setEnabled(true);
      this.dateFieldStartDateApi.setEnabled(true);
      this.dateFieldEndDateApi.setEnabled(true);
      this.autocompletePathologieApi.setEnabled(true);
      this.radioGroupIntensitySeveryAPI.setEnabled(true);
      this.checkboxInsertProblemsApi.setEnabled(true);
      this.textAreaApproachApi.setEnabled(true);
    }
  }

  private createNewDisease(body: Record<string, Value>): void {
    this.formOneDiseasesCreateAPI.setLoading(true);
    HttpService.post(Endpoint.CREATE_HEALTH_HISTORY_DISEASES, undefined, { body })
      .then(response => {
        const { idHealthHistory } = response.body as HealthHistoryDiseasesResponse;
        this.showModalSubmit(idHealthHistory);
      })
      .catch((err) => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => this.formOneDiseasesCreateAPI.setLoading(false));
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
  }

  private releaseDisease(idHealthHistory: number): void {
    this.formOneDiseasesCreateAPI.setLoading(true);
    HttpService.put(Endpoint.RELEASE_HEALTH_HISTORY_DISEASES, { sequenceNumberDisease: idHealthHistory })
      .then(() => {
        this.formOneDiseasesCreateAPI.setLoading(true);
        this.goBack();
      }).catch((err) => {
        this.showToast(err?.body, ToastLevel.ERROR);
        this.formOneDiseasesCreateAPI.setLoading(false)
      });
  }

  private setRadioGroupData(expressionId: number, radioGroup: RadioGroupAPI): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    HttpService.get(Endpoint.GET_PAIN_SCALE_DATA, { encounterId, expressionId, patientId })
      .then(response => {
        const responseBody = response.body as SelectableItem[];
        radioGroup.setData(responseBody);
        radioGroup.setValue(responseBody[0]);
      }).catch((error) => {
        this.showToast(error?.body, ToastLevel.ERROR)
      });
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'health-history-diseases',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private showModalSubmit(idHealthHistory: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.release-title-evolution',
      onClickCancel: () => { this.historyService.back(); },
      onClickOk: () => { this.releaseDisease(idHealthHistory) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    });
  }

  @OnBeforeSubmit({ target: HealthHistoryDiseasesCreateAliases.WIZARD })
  handleBeforeSubmit(): void {
    const { patientId, idHealthHistory, encounterId } = this.historyService.getCurrentRouteParams();
    const formDataOne = this.formOneDiseasesCreateAPI.getRecord();
    const formDataTwo = this.formTwoDiseasesCreateAPI.getRecord();
    const formDataThree = this.formThreeDiseasesCreateAPI.getRecord();

    const endDate = formDataTwo.endDate ? moment(String(formDataTwo.endDate)).utc().format(ISO_FULL_DATE_FORMAT) : '';
    const startDate = formDataTwo.startDate ? moment(String(formDataTwo.startDate)).utc().format(ISO_FULL_DATE_FORMAT) : '';
    const recordDate = formDataOne.recordDate ? moment(String(formDataOne.recordDate)).utc().format(ISO_FULL_DATE_FORMAT) : '';
    const displayPatientAlerts = BooleanUtils.isPositiveValue(this.checkboxAlertsAPI.getValue());
    const approach = formDataThree.approachDescription;
    const deniesDesiases = BooleanUtils.isPositiveValue(formDataOne.deniesDiseases as boolean);
    const diseaseAvailableDescription = formDataOne.diseaseAvailableDescription;
    const disease = formDataOne.disease ? formDataOne.disease as DiseasesType : '';
    const sae = formDataOne.sae as SaeSearch;
    const status = formDataOne.status as DomainType;
    const insertDiagnosis = BooleanUtils.isPositiveValue(formDataThree.insertDiagnosis as boolean);
    const problemList = BooleanUtils.isPositiveValue(formDataThree.insertProblemsList as boolean);
    const securityLevel = formDataOne.securityLevel as SearchType;
    const pathology = formDataTwo.pathology as DomainType;
    const intensity = this.radioGroupIntensitySeveryAPI.getValue() as RadioType;
    const notes = formDataThree.notes || '';
    const user = foundationContext.getUser() as unknown as HealthProfessionalUser;

    const body = {
      approach,
      attendance: encounterId as string,
      deniesDesiases,
      diseaseAvailableDescription,
      diseaseCode: disease ? disease.diseaseCode : '',
      displayPatientAlerts,
      endDate,
      idHealthHistory: idHealthHistory ? idHealthHistory as string : '',
      insertDiagnosis,
      intensity: intensity?.value ? intensity.value : '',
      notes: notes ? notes : '',
      pathologyCode: pathology?.valueDomain ? pathology.valueDomain : '',
      physicalPerson: patientId,
      problemList,
      professional: user.physicalPersonId,
      recordDate,
      saeCode: sae ? sae.id : '',
      securityLevelCode: securityLevel?.id ? securityLevel.id.valueOf() : '',
      startDate,
      status: status ? status.valueDomain : ''
    } as Record<string, Value>;
    this.createNewDisease(body);
  }

  @OnUnmount(HealthHistoryDiseasesCreateAliases.LAYOUT_HEALTH_HISTORY_DISEASES_CREATE)
  handleUnmount(): void {
    HealthHistoryListContext.clean();
    HealthHistoryListContext.currentTab = HealthHistoryType.DISEASES;
  }
}
